/**
 * Retrieve the textCardElements for an accountCard
 *
 * @param {Object} options The options.
 * @param {Object} options.accountCard the accountCard
 * @param {Boolean} options.emptyOnly whether to return only text elements whose content is empty
 * @returns {Array}
 */
import { CARD_ELEMENT_TYPE } from '../../../../../../../../../../../../services/cards/constants';

export const getTextCardElements = ({ accountCard, emptyOnly = false }) => {
  if (!accountCard) {
    return [];
  }

  const accountCardSide = accountCard.account_card_sides?.[0];

  const textCardElements = accountCardSide.card_side.card_template.card_elements.filter(
    element => element.type === CARD_ELEMENT_TYPE.TEXT,
  );

  if (emptyOnly) {
    return textCardElements.filter(element => {
      const cardSideConfig = accountCardSide.card_side.card_element_configurations.find(
        config => config.card_element.id === element.id,
      );
      const elementConfig = accountCardSide.card_element_configurations.find(
        config => config.card_element.id === element.id,
      );

      return cardSideConfig.is_editable && !elementConfig?.text;
    });
  }

  return textCardElements;
};

/**
 * Determines whether PrintProductsEmptyTextAlert should fire based on the state
 * of empty TEXT cardElementConfigurations.
 *
 * @param {Object} options The options.
 * @param {Object} options.accountCard The current accountCard
 * @returns {boolean}
 */
export const shouldAlertEmptyText = ({ accountCard }) => {
  return getTextCardElements({ accountCard, emptyOnly: true }).length > 0;
};

/**
 * Retrieve the photoCardElements for an accountCard
 *
 * @param {Object} options The options.
 * @param {Object} options.accountCard the accountCard
 * @param {Boolean} options.emptyOnly whether to return only photo elements whose content is empty
 * @returns {Array}
 */
export const getPhotoCardElements = ({ accountCard, emptyOnly = false }) => {
  if (!accountCard) {
    return [];
  }

  const accountCardSide = accountCard.account_card_sides?.[0];

  const photoCardElements = (
    accountCardSide?.card_side?.card_template?.card_elements ?? []
  ).filter(element => element.type === CARD_ELEMENT_TYPE.PHOTO);

  if (emptyOnly) {
    return photoCardElements.filter(element => {
      const elementConfig = accountCardSide.card_element_configurations.find(
        config => config.card_element.id === element.id,
      );

      // Covers for non editable config being setup already.
      // Some img. elements may be used here.
      const nonEditableConfig = accountCardSide.card_side.card_element_configurations.find(
        config => config.card_element.id === element.id && !config.is_editable,
      );

      return !elementConfig && !nonEditableConfig;
    });
  }

  return photoCardElements;
};

/**
 * Determines whether or not to tell the user pics are missing.
 *
 * @param {Object} options The options.
 * @param {Object} options.accountCard The current accountCard
 * @returns {boolean}
 */
export const shouldAlertEmptyPhotos = ({ accountCard }) => {
  return getPhotoCardElements({ accountCard, emptyOnly: true }).length > 0;
};
